import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useJourney } from "@/context/JourneyContext";
import { subjectChoicesState, subjectCountText } from "@/lib/subjectChoices";

const ACCENT = "#002147";

function useShortlistCount(signedIn) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!signedIn) return;
    api.get("/shortlist").then((r) => setCount((r.data?.items || []).length)).catch(() => setCount(0));
  }, [signedIn]);
  return count;
}

// Course stop on Quick Check: saves the student's subject choices in place, never navigates.
export function SubjectChoicesPanel({ subjects, cap = 5, onExplore, signedIn }) {
  const { confirmPillar, profile } = useJourney();
  const [status, setStatus] = useState("idle");
  const shortlistCount = useShortlistCount(signedIn);
  const st = subjectChoicesState({ subjects, journeyProfile: profile });
  const over = subjects.length > cap;
  const key = subjects.join("|");
  useEffect(() => { setStatus((s) => (s === "saving" ? s : "idle")); }, [key]);

  const save = async () => {
    if (over || st.same || status === "saving") return;
    setStatus("saving");
    const res = await confirmPillar("prove", subjects.map((l) => ({ slug: l, name: l, label: l })));
    setStatus(res?.ok ? "saved" : "error");
  };

  return (
    <div data-testid="qc-subjects-panel" className="rounded-xl border-2 border-dashed p-4 lg:p-5" style={{ borderColor: ACCENT + "66", background: ACCENT + "08" }}>
      <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: ACCENT }}>Your subjects</div>
      <div className="text-[13px] text-stone-800 font-semibold mt-1" data-testid="qc-subjects-count">{subjectCountText(subjects.length, cap)}</div>
      <ol className="flex flex-wrap gap-1.5 mt-1" data-testid="qc-subjects-list">
        {subjects.map((s, i) => (
          <li key={s} data-testid={`qc-subjects-item-${i}`} className="inline-flex items-center rounded-full bg-white/70 border border-stone-200 px-2 py-0.5 text-[11.5px] text-stone-700">
            <span className="font-mono text-stone-400 mr-1">{i + 1}.</span>{s}
          </li>
        ))}
      </ol>
      {over && (
        <p className="text-[12px] text-rose-700 mt-2" data-testid="qc-subjects-over-cap">
          You've selected {subjects.length}; the most you can save is {cap}. Remove {subjects.length - cap} to save your choices.
        </p>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button type="button" onClick={save} disabled={over || st.same || status === "saving"} data-testid="qc-subjects-save"
          className="rounded-md text-white min-h-[44px] px-4" style={!over && !st.same ? { backgroundColor: ACCENT } : {}}>
          {status === "saving" ? "Saving…" : st.label}
          {st.same && status !== "saving" && <Check className="h-4 w-4 ml-1.5" aria-hidden="true" />}
        </Button>
      </div>
      <div aria-live="polite" className="mt-2 text-[12.5px]">
        {status === "saved" && st.same && (
          <p className="text-emerald-800" data-testid="qc-subjects-saved-note">Saved to your journey: {subjects.join("; ")}.</p>
        )}
        {status === "error" && (
          <p role="alert" className="text-rose-700" data-testid="qc-subjects-save-error">
            Couldn't save your subject choices. They're still selected here, so please try again.
          </p>
        )}
      </div>
      <SubjectNextAction onExplore={onExplore} shortlistCount={shortlistCount} />
    </div>
  );
}

function SubjectNextAction({ onExplore, shortlistCount }) {
  return (
    <div className="mt-3 border-t border-stone-200 pt-3" data-testid="qc-subjects-next">
      <p className="text-[12.5px] text-stone-600">Choose courses to explore, then add the ones you like to your shortlist.</p>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2">
        <button type="button" onClick={onExplore} data-testid="qc-subjects-explore"
          className="inline-flex min-h-[44px] items-center gap-1.5 text-sm font-semibold text-[#002147] underline underline-offset-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#002147] rounded">
          Explore courses for these subjects <ArrowDown className="h-4 w-4 shrink-0" aria-hidden="true" />
        </button>
        {shortlistCount > 0 && (
          <Link to="/shortlist" data-testid="qc-subjects-view-shortlist"
            className="inline-flex min-h-[44px] items-center text-sm text-stone-700 underline underline-offset-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#002147] rounded">
            View my shortlist ({shortlistCount})
          </Link>
        )}
      </div>
    </div>
  );
}
